import { isEmpty } from 'lodash';
import { BaseTicketController } from '../../../../common/base/ticketController';
import { financesStateName } from '../finances/finances.state';

export class WaitForTransferringController extends BaseTicketController {
  constructor(
    controllerService,
    ticketService,
    receivingCreateRequestStateName,
    $q,
    $scope,
  ) {
    'ngInject';
    super({
      controllerService,
      ticketService,
      receivingCreateRequestStateName,
      $q,
      $scope,
    });
  }

  get deadline() {
    return this.isFeatureActive(
      this.FeatureNames.FF_HIDE_DEADLINE_FOR_WAITING_FOR_TRANSFERRING,
    )
      ? null
      : this.ticket.nextDeadlineInBankDays;
  }

  get hasUploadPrivacyCheckbox() {
    return this.isFeatureActive(
      this.FeatureNames.FF_BALANCE_LIST_UPLOAD_PRIVACY_COMPLIANT,
    );
  }

  get showBalanceListValidation() {
    return (
      this.isFeatureActive(
        this.FeatureNames.FF_VALIDATE_IBANS_BEFORE_UPLOADING_BALANCE_LIST,
      ) && this.selectedUploadAction === 'balanceList'
    );
  }

  get documentTypes() {
    return this.config.ui.waitForTransferringUploadOptions;
  }

  get uploadBtnLabel() {
    return `app.waitForTransferring.submit.${this.selectedUploadAction}.label`;
  }

  get fileUploadTitle() {
    return `app.finances.upload.${this.selectedUploadAction}.title`;
  }

  get fileUploadDescription() {
    return `app.finances.upload.${this.selectedUploadAction}.body`;
  }

  selectUploadAction(uploadAction) {
    this.selectedUploadAction = uploadAction;
    this.uploadPrivacyConfirmed = false;
    this.uploadedPdf = null;
    this.uploadedPdfPreview = null;
  }

  $onInit() {
    super.$onInit();

    this.model = {};

    // Set default value for the entry date to today
    this.model.entryDate = new Date();

    // When the ticket is rejected, navigate away
    if (this.isRejected()) {
      this.navigateToRejected();
    }

    // Set production flag
    this.isProduction = this.envService.isProduction();
  }

  isRejected() {
    return this.ticketService.hasStatus(
      this.ticket,
      this.ticketService.TicketStates.RECEIVED_REJECTION,
    );
  }

  uploadRejection() {
    const { entryDate } = this.model;
    return this.ticketService
      .uploadRejection(this.ticket.id, this.uploadedPdf, entryDate)
      .busy(this, 'pdfUploadingBusy')
      .then((resp) => {
        this.rejectReceiving();
      });
  }

  submitTransactionData() {
    return this.ticketService
      .uploadTransactionData(this.ticket.id, this.uploadedPdf)
      .busy(this, 'pdfUploadingBusy');
  }

  submitPdf() {
    if (this.selectedUploadAction === 'balanceList') {
      this.submitTransactionData();
    } else if (this.selectedUploadAction === 'rejection') {
      this.uploadRejection();
    } else if (this.selectedUploadAction === 'other') {
      this.uploadMiscData();
    }
  }

  get hasMiscData() {
    return !isEmpty(this.uploadedMiscData);
  }

  uploadPdf(file) {
    const reader = new FileReader();
    this.uploadedPdf = file;

    reader.onload = () => {
      const base64String = reader.result;
      this.$scope.$apply(() => {
        this.uploadedPdfPreview = base64String;
      });
    };

    reader.readAsDataURL(file);

    return Promise.resolve(file);
  }

  uploadMiscData() {
    return this.ticketService.uploadMiscDocument(
      this.ticket.id,
      this.uploadedPdf,
    );
  }

  get ticketStatusTitle() {
    return this.ticket.warrantShipmentSkipped
      ? 'app.waitForTransferring.warrantShipmentSkipped.title'
      : 'app.waitForTransferring.title';
  }

  get ticketStatusBody() {
    return this.ticket.warrantShipmentSkipped
      ? 'app.waitForTransferring.warrantShipmentSkipped.body'
      : 'app.waitForTransferring.body';
  }

  navigateToRejected() {
    this.ticketService.viewStatusChanged(this.ticket.id, {
      reason: this.ticket.status,
      status: 'error',
      icon: 'fa-ban',
      cta: () => this.duplicate(),
      ctaTitle: 'app.ticket.duplicate',
    });
  }

  balanceListValidated(valid) {
    this.balanceListSuccessfullyValidated = valid;
  }

  navigateToFinances() {
    this.go(financesStateName);
  }

  get disableSubmitButton() {
    return (
      !this.uploadedPdf ||
      (!this.uploadPrivacyConfirmed && this.hasUploadPrivacyCheckbox) ||
      (!this.balanceListSuccessfullyValidated && this.showBalanceListValidation)
    );
  }

  rejectReceiving() {
    return this.ticketService
      .rejectReceiving(this.ticket.id)
      .busy(this, 'pdfUploadingBusy')
      .then(() => this.navigateToRejected());
  }

  duplicate() {
    const { receivingCreateRequestStateName, appCache, ticket } = this;

    appCache.put('duplicateTicket', ticket.formValues);
    this.go(receivingCreateRequestStateName);
  }

  simulateFinances() {
    const { ticketService, ticket } = this;
    this.simulateFinancesBusy = true;
    ticketService.simulateFinancesDataCaptured(ticket.id).then((result) => {
      this.simulateFinancesBusy = false;
      ticketService.getRefreshResponse(ticket.id)(result);
    });
  }

  uploadDemoData(file) {
    return this.ticketService
      .uploadStandingOrdersDemoData(this.ticket.id, file)
      .busy(this, 'isDemoUploadBusy')
      .then((resp) => {
        return resp;
      });
  }

  simulateFinancesLegacy() {
    const { ticketService, ticket } = this;
    this.simulateFinancesBusy = true;
    ticketService.simulateFinancesDataCaptured(ticket.id).then((result) => {
      this.simulateFinancesBusy = false;
      ticketService.getRefreshResponse(ticket.id)(result);
    });
  }
}
